import React, { useState, useMemo } from "react";
import {
  useGetStagingTemplateDetailQuery,
  useGetProdTemplateDetailQuery,
  useUpdateStagingTemplateMutation,
  useUpdateProdTemplateMutation,
  useDeleteStagingTemplateMutation,
  useDeleteProdTemplateMutation,
} from "../../../../app/api/console/scanTemplateSyncApiSlice";
import { useGetWebsitesListQuery } from "../../../../app/api/platform/platformWebsitesApiSlice";
import { TemplateTypeBadge, ScanModeBadge } from "./badges";
import PromptsTab from "./tabs/PromptsTab";
import TestCasesTab from "./tabs/TestCasesTab";
import SettingsTab from "./tabs/SettingsTab";
import HistoryTab from "./tabs/HistoryTab";
import SyncDiffPanel from "./SyncDiffPanel";

const TABS = [
  { id: "prompts", label: "Prompts", icon: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" },
  { id: "test-cases", label: "Test Cases", icon: "M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" },
  { id: "settings", label: "Settings", icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z" },
  { id: "history", label: "History", icon: "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" },
];

const TemplateDetail = ({ templateId, environment, onDeleted, onDuplicate, syncStatus }) => {
  const [activeTab, setActiveTab] = useState("prompts");
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: stagingDetail, isLoading: isLoadingStaging } = useGetStagingTemplateDetailQuery({ templateId }, { skip: environment !== "staging" });
  const { data: prodDetail, isLoading: isLoadingProd } = useGetProdTemplateDetailQuery({ templateId }, { skip: environment !== "prod" });

  const [updateStagingTemplate, { isLoading: isUpdatingStaging }] = useUpdateStagingTemplateMutation();
  const [updateProdTemplate, { isLoading: isUpdatingProd }] = useUpdateProdTemplateMutation();
  const [deleteStagingTemplate, { isLoading: isDeletingStaging }] = useDeleteStagingTemplateMutation();
  const [deleteProdTemplate, { isLoading: isDeletingProd }] = useDeleteProdTemplateMutation();
  const isUpdating = environment === "staging" ? isUpdatingStaging : isUpdatingProd;
  const isDeleting = environment === "staging" ? isDeletingStaging : isDeletingProd;

  const { data: websites = [] } = useGetWebsitesListQuery({ useProd: environment === "prod" });
  const websiteMap = useMemo(() => {
    if (!websites?.length) return {};
    return websites.reduce((acc, w) => { acc[w.website_id] = w.website_name || w.website_id; return acc; }, {});
  }, [websites]);

  const template = environment === "staging" ? stagingDetail : prodDetail;
  const isLoading = environment === "staging" ? isLoadingStaging : isLoadingProd;

  const websitePromptIds = useMemo(() => {
    if (!template?.website_prompts) return [];
    return template.website_prompts.map((p) => p.website_id);
  }, [template]);

  const handleStartEdit = () => {
    setEditForm({
      title: template.title || "", description: template.description || "",
      time_minutes: template.time_minutes || 60, plan_type: template.plan_type || "core",
      automation_ready: template.automation_ready || false,
      is_most_popular: template.is_most_popular ?? false,
      show_test_cases: template.show_test_cases ?? false,
      scan_mode: template.scan_mode || "scraper_one_look",
      requires_file: template.requires_file ?? false,
      requires_scope: template.requires_scope ?? false,
      field_requirements: template.field_requirements ?? null,
      customers_can_run: template.customers_can_run ?? false,
      launch_prompt: template.launch_prompt ?? "",
      categories: template.categories || [], tags: template.tags || [],
      description_items: template.description_items || [],
    });
    setIsEditing(true);
    setActiveTab("settings");
  };

  const handleSaveEdit = async () => {
    const updateFn = environment === "staging" ? updateStagingTemplate : updateProdTemplate;
    await updateFn({ templateId, updates: editForm });
    setIsEditing(false);
    setEditForm({});
  };

  const handleDelete = async () => {
    const deleteFn = environment === "staging" ? deleteStagingTemplate : deleteProdTemplate;
    await deleteFn({ templateId });
    setShowDeleteConfirm(false);
    if (onDeleted) onDeleted();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-purple border-t-transparent mx-auto" />
          <p className="text-body-sm text-text-tertiary mt-3">Loading template...</p>
        </div>
      </div>
    );
  }
  if (!template) {
    return <div className="text-center py-12 text-body-sm text-text-tertiary">Template not found in {environment}</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-body-lg font-semibold text-text-primary">{template.title}</h2>
            <ScanModeBadge mode={template.scan_mode} />
            <TemplateTypeBadge type={template.template_type} />
            <span className={`px-2 py-0.5 rounded-md text-body-xs font-medium ${environment === "staging" ? "bg-accent-purple/10 text-accent-purple" : "bg-severity-medium/10 text-severity-medium"}`}>
              {environment}
            </span>
          </div>
          {template.description && (
            <p className="text-body-sm text-text-secondary mt-1">{template.description}</p>
          )}
        </div>
        <div className="flex gap-2 ml-4 flex-shrink-0">
          {isEditing ? (
            <>
              <button onClick={handleSaveEdit} disabled={isUpdating} className="px-4 py-2 bg-button-primary text-white rounded-xl text-body-xs font-semibold hover:bg-button-primary-hover disabled:opacity-40 transition-colors">
                {isUpdating ? "Saving..." : "Save Changes"}
              </button>
              <button onClick={() => { setIsEditing(false); setEditForm({}); }} className="px-4 py-2 border border-border-default rounded-xl text-body-xs text-text-secondary hover:bg-surface-neutral transition-colors">
                Cancel
              </button>
            </>
          ) : (
            <>
              <button onClick={handleStartEdit} className="px-3 py-2 border border-border-default text-text-secondary rounded-xl text-body-xs font-medium hover:bg-surface-neutral transition-colors">
                Edit Settings
              </button>
              {onDuplicate && (
                <button
                  onClick={() => onDuplicate(template)}
                  className="px-3 py-2 border border-border-default text-text-secondary rounded-xl text-body-xs font-medium hover:bg-surface-neutral transition-colors flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.5a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                  </svg>
                  Duplicate
                </button>
              )}
              <button onClick={() => setShowDeleteConfirm(true)} className="px-3 py-2 border border-severity-high/20 text-severity-high rounded-xl text-body-xs font-medium hover:bg-severity-high/5 transition-colors">
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl border border-border-subtle">
            <h3 className="text-body-lg font-semibold text-text-primary mb-2">Delete Template</h3>
            <p className="text-body-sm text-text-secondary mb-5">Are you sure you want to delete <strong>{template.title}</strong>? This cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 border border-border-default rounded-xl text-body-sm text-text-secondary hover:bg-surface-neutral transition-colors">Cancel</button>
              <button onClick={handleDelete} disabled={isDeleting} className="px-4 py-2 bg-severity-high text-white rounded-xl text-body-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-all">{isDeleting ? "Deleting..." : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Sync diff panel */}
      <SyncDiffPanel templateId={templateId} syncStatus={syncStatus} />

      {/* Tabs */}
      <div className="flex gap-0.5 mb-5 border-b border-border-subtle">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-body-xs font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? "border-text-primary text-text-primary"
                : "border-transparent text-text-tertiary hover:text-text-secondary"
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
            </svg>
            {tab.label}
            {tab.id === "prompts" && template.base_prompt && (
              <span className="px-1.5 py-0.5 rounded-md text-caption bg-accent-purple/10 text-accent-purple font-semibold">
                {1 + (template.website_prompts?.length || 0)}
              </span>
            )}
            {tab.id === "test-cases" && (template.test_cases_containers?.length || 0) > 0 && (
              <span className="px-1.5 py-0.5 rounded-md text-caption bg-accent-pink/10 text-accent-pink font-semibold">
                {template.test_cases_containers.reduce((sum, c) => sum + (c.current_test_cases?.length || 0), 0)}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "prompts" && (
        <PromptsTab
          template={template}
          environment={environment}
          templateId={templateId}
          websiteMap={websiteMap}
          websitePromptIds={websitePromptIds}
        />
      )}
      {activeTab === "test-cases" && (
        <TestCasesTab
          template={template}
          environment={environment}
          templateId={templateId}
          websiteMap={websiteMap}
        />
      )}
      {activeTab === "settings" && (
        <SettingsTab
          template={template}
          isEditing={isEditing}
          editForm={editForm}
          setEditForm={setEditForm}
        />
      )}
      {activeTab === "history" && (
        <HistoryTab
          template={template}
          websiteMap={websiteMap}
        />
      )}
    </div>
  );
};

export default TemplateDetail;
